import { useCallback, useState } from "react";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { remittanceApi } from "../../../api/remittanceApi";
import { remittanceActions } from "../../../state/remittanceSlice";
import { AppStrings, logApiErrorToSentry } from "../../../utils";
import { getAdIIApiError, mergeAdIIKycProfileDetails } from "../../../utils/remitterKycFlow";

const LINK_SENT_MESSAGE = "Link sent to both email and phone";

export const useAdIIKycLinkActions = () => {
  const dispatch = useAppDispatch();
  const { remitter } = useAppSelector((state) => state.remittance);
  const authState = useAppSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [linkSentLocally, setLinkSentLocally] = useState(false);

  const remitterUserKey = remitter?.selectedRemitter?.userKey;
  const loggedInUserKey = authState?.currentUser?.vendor_key ?? authState?.userKey ?? "";
  const profileDetails = remitter?.selectedRemitter?.adIIKycProfileDetails;

  const updateProfile = useCallback(
    (profileData: any) => {
      if (!profileData) return;

      dispatch(
        remittanceActions.setSelectedRemitter({
          ...remitter?.selectedRemitter,
          adIIKycProfileDetails: mergeAdIIKycProfileDetails(profileDetails, profileData),
        })
      );
    },
    [dispatch, profileDetails, remitter?.selectedRemitter]
  );

  const sendLink = useCallback(async () => {
    if (!remitterUserKey || !loggedInUserKey || loading) return;

    setLoading(true);
    try {
      const searchResponse = await remittanceApi.searchAdIICkyc({
        userKey: remitterUserKey,
        requestType: "k2",
        loggedInUserKey,
      });

      const journeyId = searchResponse?.data?.journeyId;
      if (!journeyId) {
        throw new Error("Journey ID missing from CKYC search response");
      }

      const profileResponse = await remittanceApi.createAdIIVkycProfile({
        journeyId,
        userKey: remitterUserKey,
        requestType: "k2",
        loggedInUserKey,
        sendType: "both",
      });

      updateProfile(profileResponse?.data);
      setLinkSentLocally(true);
      toast.success(LINK_SENT_MESSAGE);
    } catch (err: any) {
      toast.error(getAdIIApiError(err, AppStrings.GENERIC_ERROR));
      logApiErrorToSentry(err, "sendAdIIKycLink");
    } finally {
      setLoading(false);
    }
  }, [loading, loggedInUserKey, remitterUserKey, updateProfile]);

  const generateNewLink = useCallback(async () => {
    const uniqueKey = profileDetails?.uniqueKey;
    if (!uniqueKey || loading) return;

    setLoading(true);
    try {
      const response = await remittanceApi.generateAdIIVkycNewLink({
        uniqueKey,
        sendType: "both",
      });
      updateProfile(response?.data);
      setLinkSentLocally(true);
      toast.success(LINK_SENT_MESSAGE);
    } catch (err: any) {
      toast.error(getAdIIApiError(err, AppStrings.GENERIC_ERROR));
      logApiErrorToSentry(err, "generateAdIIKycNewLink");
    } finally {
      setLoading(false);
    }
  }, [loading, profileDetails?.uniqueKey, updateProfile]);

  const resendLink = useCallback(async () => {
    if (!remitterUserKey || loading) return;

    setLoading(true);
    try {
      const response = await remittanceApi.resendAdIIVkycLink({
        userKey: remitterUserKey,
      });
      updateProfile(response?.data);
      setLinkSentLocally(true);
      toast.success(LINK_SENT_MESSAGE);
    } catch (err: any) {
      toast.error(getAdIIApiError(err, AppStrings.GENERIC_ERROR));
      logApiErrorToSentry(err, "resendAdIIKycLink");
    } finally {
      setLoading(false);
    }
  }, [loading, remitterUserKey, updateProfile]);

  const reset = useCallback(() => {
    setLoading(false);
    setLinkSentLocally(false);
  }, []);

  return {
    loading,
    linkSentLocally,
    profileDetails,
    sendLink,
    generateNewLink,
    resendLink,
    reset,
  };
};
